import React from "react";
import { motion } from "framer-motion";
import { API_URLS } from "../../App";
import { useLayoutContext } from "../../context/LayoutContext";
import api from "../../hooks/api";

interface ManageOrderActionsProps {
  order: Order;
  handleConfirmOrder: (orderId: string) => void;
  handleRejectOrder: (
    orderId: string,
    reason: string,
    notes: string,
    visibleToReseller: boolean,
  ) => void;
  handleCancelOrder: (
    orderId: string,
    reason: string,
    notes: string,
    visibleToReseller: boolean,
  ) => void;
  onOpenRejectModal: () => void;
  onOpenCancelModal: () => void;
  onResubmitToErp: (orderId: string, acknowledgeDuplicateRisk: boolean) => void;
}

const ManageOrderActions: React.FC<ManageOrderActionsProps> = ({
  order,
  handleConfirmOrder,
  onOpenRejectModal,
  onOpenCancelModal,
  onResubmitToErp,
}: ManageOrderActionsProps) => {
  const { setError, setLoading, userSettings } = useLayoutContext();

  const handleExportExcel = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        API_URLS.ORDERS + "/_actions/export-excel",
        order,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `encomenda_${order.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.warn("Erro ao exportar encomenda para Excel:", error);
      setError(error.message || "Erro ao exportar encomenda para Excel.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <footer className="p-4">
      <div className="flex is-justify-content-center">
        {(userSettings?.isAdmin() || userSettings?.isCommercial()) && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="bg-green-700 hover:bg-green-800 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
            onClick={handleExportExcel}
          >
            Exportar Excel
          </motion.button>
        )}
        {userSettings?.isAdmin() &&
          ["CONFIRMED", "SHIPPED", "COMPLETED"].includes(order.status) &&
          order.erp_submission_status !== "SENT" &&
          order.erp_submission_status !== "PENDING" &&
          order.erp_submission_status !== "SENDING" && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
              onClick={() =>
                onResubmitToErp(
                  order.id,
                  order.erp_submission_status === "UNKNOWN",
                )
              }
            >
              {order.erp_submission_status === "FAILED" ||
              order.erp_submission_status === "UNKNOWN"
                ? "Reenviar para o ERP"
                : "Enviar para o ERP"}
            </motion.button>
          )}
        {order.status === "PENDING" && (
          <>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`bg-green-700 hover:bg-green-800 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4`}
              onClick={() => handleConfirmOrder(order.id)}
            >
              Aceitar Encomenda
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-red-400 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
              onClick={onOpenRejectModal}
            >
              Rejeitar Encomenda
            </motion.button>
          </>
        )}
        {order.status === "CONFIRMED" && (
          <>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
            >
              Enviar Encomenda
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-orange-500 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
              onClick={onOpenCancelModal}
            >
              Cancelar Encomenda
            </motion.button>
          </>
        )}
        {order.status === "SHIPPED" && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="bg-orange-500 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
            onClick={onOpenCancelModal}
          >
            Cancelar Encomenda
          </motion.button>
        )}
      </div>
    </footer>
  );
};

export default ManageOrderActions;
